import { useState } from 'react';
import type { FlightOffer } from '../types/flight';
import {
  Plane,
  Clock,
  MapPin,
  Users,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Plus,
  ExternalLink,
  Wifi,
  Utensils,
  Monitor,
  Armchair
} from 'lucide-react';

interface FlightCardProps {
  flight: FlightOffer;
  onSelect?: (flight: FlightOffer) => void;
  onAddToItinerary?: (flight: FlightOffer) => void;
  isSelected?: boolean;
  showDetails?: boolean;
  className?: string;
}

// Format duration from minutes
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}

// Format time from ISO string
function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

// Format date from ISO string
function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Get airline logo URL (placeholder)
function getAirlineLogoUrl(airline: string): string {
  // In production, this would fetch from an airline logo API
  return `https://picsum.photos/seed/${airline}/100/50`;
}

// Get amenity icon
function getAmenityIcon(amenity: string) {
  const amenityIcons: Record<string, typeof Wifi> = {
    'Wi-Fi': Wifi,
    'Entertainment': Monitor,
    'Meals': Utensils,
    'Extra Legroom': Armchair,
  };
  
  const Icon = amenityIcons[amenity] || Wifi;
  return <Icon className="h-4 w-4" />;
}

export default function FlightCard({
  flight,
  onSelect,
  onAddToItinerary,
  isSelected = false,
  showDetails = false,
  className = '',
}: FlightCardProps) {
  const [isExpanded, setIsExpanded] = useState(showDetails);

  const {
    airline,
    flightNumber,
    origin,
    destination,
    departureTime,
    arrivalTime,
    duration,
    stops,
    price,
    seatsAvailable,
    cabinClass,
    amenities = [],
    layovers = [],
  } = flight;

  const stopLabel = stops === 0 
    ? 'Direct' 
    : stops === 1 
      ? '1 stop' 
      : `${stops} stops`;

  return (
    <div 
      className={`p-4 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-primary-50 border-2 border-primary-500' : 'border-b border-gray-100'
      } ${className}`}
    >
      {/* Main Content */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Airline Info */}
        <div className="flex items-center gap-3 lg:w-32">
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
            <Plane className="h-6 w-6 text-gray-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{airline}</p>
            <p className="text-sm text-gray-500">{flightNumber}</p>
          </div>
        </div>

        {/* Route & Times */}
        <div className="flex-1">
          <div className="flex items-center gap-4">
            {/* Departure */}
            <div className="text-center lg:text-left">
              <p className="text-2xl font-bold text-gray-900">{formatTime(departureTime)}</p>
              <p className="text-sm font-medium text-gray-700">{origin.code}</p>
              <p className="text-xs text-gray-500">{origin.city}</p>
            </div>

            {/* Duration & Stops */}
            <div className="flex-1 px-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-300 relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-center text-gray-500 mt-1">
                {formatDuration(duration.totalMinutes)} • {stopLabel}
              </p>
            </div>

            {/* Arrival */}
            <div className="text-center lg:text-right">
              <p className="text-2xl font-bold text-gray-900">{formatTime(arrivalTime)}</p>
              <p className="text-sm font-medium text-gray-700">{destination.code}</p>
              <p className="text-xs text-gray-500">{destination.city}</p>
            </div>
          </div>

          {/* Date */}
          <p className="text-sm text-gray-500 mt-2">
            {formatDate(departureTime)}
            {stops > 0 && layovers.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-2 text-primary-600 hover:underline flex items-center gap-1 inline-flex"
              >
                {isExpanded ? (
                  <>
                    Hide layovers <ChevronUp className="h-3 w-3" />
                  </>
                ) : (
                  <>
                    Show layovers <ChevronDown className="h-3 w-3" />
                  </>
                )}
              </button>
            )}
          </p>
        </div>

        {/* Price & Actions */}
        <div className="flex flex-col lg:items-end gap-2 lg:w-40">
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              ${price.total.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">
              per person
            </p>
          </div>

          <div className="flex gap-2">
            {onAddToItinerary && (
              <button
                onClick={() => onAddToItinerary(flight)}
                className="flex-1 lg:flex-none px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center justify-center gap-1 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            )}
            {onSelect && (
              <button
                onClick={() => onSelect(flight)}
                className={`flex-1 lg:flex-none px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isSelected
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}
              >
                Select
              </button>
            )}
          </div>

          <p className={`text-xs ${seatsAvailable <= 5 ? 'text-orange-600 font-medium' : 'text-gray-400'}`}>
            {seatsAvailable <= 5 ? `Only ${seatsAvailable} seats left!` : `${seatsAvailable} seats available`}
          </p>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && layovers.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Connection Details</h4>
          <div className="space-y-3">
            {layovers.map((layover, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-1 border-l-2 border-gray-300 pl-3">
                  <p className="text-sm font-medium text-gray-700">
                    {layover.airport.code} - {layover.airport.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {layover.airport.city}, {layover.airport.country}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Layover: {formatDuration(layover.duration.totalMinutes)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Amenities */}
      {amenities.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {amenities.slice(0, 4).map((amenity, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600"
              title={amenity}
            >
              {getAmenityIcon(amenity)}
              {amenity}
            </span>
          ))}
          {amenities.length > 4 && (
            <span className="text-xs text-gray-500">
              +{amenities.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Cabin Class */}
      <div className="mt-3 flex items-center gap-2">
        <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
          {cabinClass.replace('_', ' ')}
        </span>
        {price.base < price.total && (
          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 rounded text-xs text-green-700">
            ${price.total - price.base} in taxes & fees
          </span>
        )}
      </div>
    </div>
  );
}
